import React from "react";

import {
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { currencySign } from "constants/index";
import { CurrencyType, ReportPredictionTableCell, ReportPredictionTableRow, ReportPredictionTable } from "store/types";
import { formatCurrency } from "utils/strings";

interface IProps {
  data: ReportPredictionTable | null;
  loading: boolean;
  currency: CurrencyType;
}

interface FlatRow {
  row: ReportPredictionTableRow;
  depth: number;
}

const flattenRows = (rows: ReportPredictionTableRow[], depth = 0, acc: FlatRow[] = []): FlatRow[] => {
  rows.forEach((row) => {
    acc.push({ row, depth });
    if (row.children?.length) flattenRows(row.children, depth + 1, acc);
  });
  return acc;
};

/**
 * Read-only render of the deal-scoped cashflow prediction table. Mirrors the
 * column structure of the portfolio prediction table (years → measures) without
 * the inline editing / scenario controls, which are out of scope for the deal view.
 */
export const ForecastPredictionTable: React.FC<IProps> = ({ data, loading, currency }) => {
  const currencySymbol = currencySign[currency];

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: 200 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!data || !data.columns.length || !data.rows.length) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: 200 }}>
        <Typography sx={{ color: "#5d7782", fontSize: "14px" }}>No forecast table data for this deal.</Typography>
      </Stack>
    );
  }

  const flatRows = flattenRows(data.rows);

  return (
    <TableContainer sx={{ borderRadius: "8px", border: "1px solid #E2E8F0" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} sx={{ fontWeight: 600, bgcolor: "#F8FAFC" }}>
              Name
            </TableCell>
            {data.columns.map((group, i) => (
              <TableCell key={i} colSpan={group.length} align="center" sx={{ fontWeight: 600, bgcolor: "#F8FAFC" }}>
                {group[0]?.key.split("|")[0]}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            {data.columns.flat().map((measure) => (
              <TableCell key={measure.key} align="right" sx={{ fontWeight: 500, bgcolor: "#F8FAFC" }}>
                {measure.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {flatRows.map(({ row, depth }, index) => (
            <TableRow key={`${row.id ?? "row"}-${index}`}>
              <TableCell sx={{ pl: `${16 + depth * 16}px`, fontWeight: depth === 0 ? 500 : 400 }}>{row.name}</TableCell>
              {data.columns.flat().map((measure) => {
                const cell = row[measure.key] as ReportPredictionTableCell | undefined;
                const value =
                  cell && cell.value != null ? formatCurrency({ value: Number(cell.value), currencySymbol }) : "—";
                return (
                  <TableCell key={measure.key} align="right">
                    {value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
